// IE Hub - JSON Help Dialog Component
// Responsive help dialog that displays FAQ content from JSON schema
// Mobile: bottom slide, full page | Desktop: right slide, partial

import React, { useRef, useState } from "react";
import {
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ExpandMoreIcon,
} from "../ui";
import { TransitionProps } from "@mui/material/transitions";

interface FAQ {
  question: string;
  answer: string;
}

interface JsonHelpDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  faqs: FAQ[];
  zIndex?: number;
}

// Slide transition for mobile (from bottom)
const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide direction="up" ref={ref} {...props} appear={props.appear ?? true} />
  );
});

export default function JsonHelpDialog({
  open,
  onClose,
  title = "Help & support",
  description,
  faqs,
  zIndex = 1302,
}: JsonHelpDialogProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Accordion state management
  const [expandedIndex, setExpandedIndex] = useState<number | false>(false);

  // Touch gesture handling for mobile swipe-to-dismiss
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Minimum swipe distance (in px) to trigger close
  const minSwipeDistance = 50;

  const handleToggle = (index: number) => (_: unknown, isExpanded: boolean) => {
    setExpandedIndex(isExpanded ? index : false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    const touch = e.targetTouches[0];
    if (touch) {
      setTouchStart(touch.clientY);
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const touch = e.targetTouches[0];
    if (!touch) return;

    const currentTouch = touch.clientY;
    const distance = currentTouch - touchStart;

    // Only allow downward swipes (positive distance)
    if (distance > 0) {
      setDragDistance(distance);
      setTouchEnd(currentTouch);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setDragDistance(0);
      return;
    }

    const distance = touchEnd - touchStart;

    // Close dialog if swiped down enough
    if (distance > minSwipeDistance) {
      onClose();
    }

    // Reset states
    setTouchStart(null);
    setTouchEnd(null);
    setIsDragging(false);
    setDragDistance(0);
  };

  const helpContent = (
    <Box data-testid="components-json-help-dialog-container-action-1"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Header with Close Button */}
      <Box data-testid="components-json-help-dialog-container-action-2"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: { xs: 2, md: 3 },
          minHeight: "64px",
        }}
      >
        <Typography data-testid="components-json-help-dialog-text-action-1"
          variant="h6"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {title}
        </Typography>
        <IconButton data-testid="components-json-help-dialog-btn-on-close"
          onClick={onClose}
          sx={{
            p: 1,
            backgroundColor: "action.hover",
            "&:hover": {
              backgroundColor: "action.selected",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Header divider — inset (mx) so the line doesn't touch the drawer borders, per Figma */}
      <Divider sx={{ mx: { xs: 2, md: 3 } }} />

      {/* Content */}
      <Box data-testid="components-json-help-dialog-container-action-3"
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          overflow: "auto",
        }}
      >
        <Stack data-testid="components-json-help-dialog-container-action-4" spacing={3}>
          {/* Description Section */}
          {description && (
            <Box data-testid="components-json-help-dialog-container-action-5">
              <Typography data-testid="components-json-help-dialog-text-action-2"
                variant="body1"
                sx={{ mb: 2, lineHeight: 1.6, color: "text.secondary" }}
              >
                {description}
              </Typography>
            </Box>
          )}

          {/* FAQs Section */}
          {faqs && faqs.length > 0 && (
            <Box data-testid="components-json-help-dialog-container-action-6">
              <Typography data-testid="components-json-help-dialog-text-frequently-asked-questions"
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
              >
                Frequently Asked Questions
              </Typography>
              <Stack data-testid="components-json-help-dialog-container-action-7" spacing={1.5}>
                {faqs.map((faq, index) => (
                  <Accordion data-testid="components-json-help-dialog-accordion-action"
                    key={index}
                    expanded={expandedIndex === index}
                    onChange={handleToggle(index)}
                    sx={{
                      boxShadow: "none",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px",
                      "&:before": {
                        display: "none",
                      },
                    }}
                  >
                    <AccordionSummary data-testid="components-json-help-dialog-accordion-summary-action"
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          m: 0,
                          my: 1.5,
                        },
                      }}
                    >
                      <Typography data-testid="components-json-help-dialog-text-action-3" variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography data-testid="components-json-help-dialog-text-action-4"
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          whiteSpace: "pre-line",
                          lineHeight: 1.7,
                        }}
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          )}

          {/* Contact Information */}
          <Box data-testid="components-json-help-dialog-container-action-8">
            <Typography data-testid="components-json-help-dialog-text-need-more-help?"
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Need more help?
            </Typography>
            <Typography data-testid="components-json-help-dialog-text-action-5"
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.5, mb: 2 }}
            >
             If you have additional questions or need assistance, you can check out our Support & FAQs page
            </Typography>
            <Box data-testid="components-json-help-dialog-container-support-and-faqs"
              component="a"
              href="/#/support-faqs"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 3,
                py: 1.25,
                backgroundColor: "#004b49",
                color: "#ffffff",
                borderRadius: "8px",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: 600,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#003634",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(0, 75, 73, 0.3)",
                },
              }}
            >
              Support and FAQ's
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );

  if (isMobile) {
    // Mobile: Full-screen dialog sliding from bottom with swipe-to-dismiss
    return (
      <Dialog data-testid="components-json-help-dialog-modal-action"
        open={open}
        onClose={onClose}
        TransitionComponent={SlideUpTransition}
        fullScreen
        keepMounted
        sx={{
          zIndex: zIndex,
          "& .MuiDialog-paper": {
            backgroundColor: "background.default",
            backgroundImage: "none",
            margin: 0,
            borderRadius: "16px 16px 0 0", // Top corners rounded for mobile
            transform: isDragging
              ? `translateY(${Math.min(dragDistance, 100)}px)`
              : "none",
            transition: isDragging ? "none" : "transform 0.3s ease-out",
            opacity: isDragging ? Math.max(1 - dragDistance / 200, 0.7) : 1,
          },
        }}
      >
        <DialogContent
          ref={dialogRef}
          sx={{ p: 0, height: "100%" }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Swipe Indicator */}
          <Box data-testid="components-json-help-dialog-container-action-9"
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: 1,
              pb: 0.5,
            }}
          >
            <Box data-testid="components-json-help-dialog-container-action-10"
              sx={{
                width: 36,
                height: 4,
                backgroundColor: "action.disabled",
                borderRadius: 2,
                opacity: 0.6,
              }}
            />
          </Box>
          {helpContent}
        </DialogContent>
      </Dialog>
    );
  }

  // Desktop: Drawer sliding from right
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      sx={{
        zIndex: zIndex,
        "& .MuiDrawer-paper": {
          width: { md: "400px", lg: "480px" },
          maxWidth: "40vw",
          backgroundColor: "background.paper",
          border: "none",
          boxShadow: theme.shadows[8],
          borderRadius: "16px 0 0 16px", // Left side rounded for desktop
        },
      }}
    >
      {helpContent}
    </Drawer>
  );
}
